import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { isSpectator, canWrite, hasSuperAdminView } from '../utils/rolePermissions';
import {
  hasGrantModuleAccess,
  hasGrantTabAccess
} from '../utils/viewPermissions';

const AuthContext = createContext();

const decodeTokenExpiry = (token) => {
  if (!token) return null;
  try {
    const [, payload] = token.split('.');
    if (!payload) return null;
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const padded = base64.padEnd(base64.length + (4 - (base64.length % 4)) % 4, '=');
    const decoded = JSON.parse(window.atob(padded));
    return decoded?.exp ? decoded.exp * 1000 : null;
  } catch (error) {
    console.error('Failed to decode token expiry:', error);
    return null;
  }
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      const expiry = decodeTokenExpiry(token);
      if (expiry && expiry <= Date.now()) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('tokenExpiry');
        delete axios.defaults.headers.common['Authorization'];
      } else {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        // Verify token is still valid
        const userData = localStorage.getItem('user');
        if (userData) {
          setUser(JSON.parse(userData));
        }
        if (expiry) {
          localStorage.setItem('tokenExpiry', expiry.toString());
        } else {
          localStorage.removeItem('tokenExpiry');
        }
        axios.get('/api/auth/me')
          .then((response) => {
            localStorage.setItem('user', JSON.stringify(response.data));
            setUser(response.data);
          })
          .catch(() => {
            // Keep cached user if refresh fails transiently.
          });
      }
    } else {
      localStorage.removeItem('tokenExpiry');
    }
    setLoading(false);
  }, []);

  const applySession = useCallback((token, userData) => {
    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify(userData));
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    const expiry = decodeTokenExpiry(token);
    if (expiry) {
      localStorage.setItem('tokenExpiry', expiry.toString());
    } else {
      localStorage.removeItem('tokenExpiry');
    }

    setUser(userData);
  }, []);

  const login = async (email, password, department) => {
    try {
      const response = await axios.post('/api/auth/login', {
        email,
        password,
        ...(department ? { department } : {})
      });
      const { token, user } = response.data;
      applySession(token, user);
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.message || 'Login failed' 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('tokenExpiry');
    delete axios.defaults.headers.common['Authorization'];
    setUser(null);
  };

  const refreshUser = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return null;

    try {
      const response = await axios.get('/api/auth/me');
      const userData = response.data;
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      return userData;
    } catch (error) {
      console.error('Failed to refresh user profile:', error);
      return null;
    }
  }, []);

  const canViewModule = useCallback(
    (moduleKey) => hasGrantModuleAccess(user?.viewGrants, moduleKey),
    [user?.viewGrants]
  );

  const canViewTab = useCallback(
    (moduleKey, tabKey) => hasGrantTabAccess(user?.viewGrants, moduleKey, tabKey),
    [user?.viewGrants]
  );

  const value = {
    user,
    login,
    applySession,
    logout,
    loading,
    refreshUser,
    viewGrants: user?.viewGrants || [],
    canViewModule,
    canViewTab,
    isReadOnly: isSpectator(user?.role),
    canWrite: canWrite(user?.role),
    hasSuperAdminView: hasSuperAdminView(user?.role)
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};